import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { Button, Tooltip } from '@mui/material';
import { colors } from '@src/styles';
import { toneButtonSX } from '../scriptsStyles';
import { useScriptsContext } from '../context/ScriptsContext';

interface RunButtonProps {
  onClick: () => void;
  isRunningScript: boolean;
}

const STATUS_CONFIG = {
  PENDING: {
    label: 'Executar',
    Icon: PlayArrowIcon,
    color: colors.primary.yellow,
    filled: true,
    disabled: false,
  },
  RUNNING: {
    label: 'Executando...',
    Icon: SyncIcon,
    color: colors.status.pending,
    filled: false,
    disabled: true,
  },
  FINISHED: {
    label: 'Executado',
    Icon: CheckCircleIcon,
    color: colors.status.concluded,
    filled: false,
    disabled: true,
  },
  BLOCKED: {
    label: 'Desbloquear',
    Icon: PlayArrowIcon,
    color: colors.status.concluded,
    filled: false,
    disabled: false,
  },
  FAILED: {
    label: 'Falhou',
    Icon: ErrorIcon,
    color: colors.primary.warning,
    filled: false,
    disabled: true,
  },
};

export const RunButton = ({ onClick, isRunningScript }: RunButtonProps) => {
  const { scriptStatus, selectedScript } = useScriptsContext();
  const status = scriptStatus ?? 'PENDING';
  const { label, Icon, color, filled, disabled } = STATUS_CONFIG[status];

  const isDisabled = disabled || isRunningScript;
  const displayLabel = isRunningScript ? 'Executando...' : label;
  const DisplayIcon = isRunningScript ? SyncIcon : Icon;

  const tooltipTitle = isRunningScript
    ? 'Aguarde enquanto o script está sendo iniciado...'
    : status === 'RUNNING'
      ? 'O script já está em execução. Aguarde a conclusão antes de executar novamente.'
      : status === 'FINISHED'
        ? 'Este script já foi concluído.'
        : status === 'FAILED'
          ? `O processamento deste script falhou e ele não pode ser executado. ${selectedScript?.execution?.failureReason ?? ''}`
          : '';

  const button = (
    <Button
      onClick={onClick}
      disabled={isDisabled}
      startIcon={<DisplayIcon />}
      sx={{
        ...toneButtonSX(color, filled),
        ...((status === 'FINISHED' || status === 'FAILED') && {
          '&.Mui-disabled': { color, borderColor: `${color}55`, backgroundColor: `${color}12` },
        }),
      }}
    >
      {displayLabel}
    </Button>
  );

  if (isDisabled && tooltipTitle) {
    return (
      <Tooltip title={tooltipTitle} arrow placement="top">
        <span>{button}</span>
      </Tooltip>
    );
  }

  return button;
};
